import { AlignerMonitoringCell, type DataTableColumn } from '@odo/shared-ui';
import type { DmMessageInboxSort } from '../../../services/dmMessagesInbox';
import type { DmInboxStatus } from '../../../services/dmNotifications';
import { NotificationAssigneeCell } from '../notifications/NotificationAssigneeCell';
import { ClosureCell } from '../shared/ClosureCell';
import { INBOX_COLUMN_CHROME, opensUpwards } from '../shared/inboxColumnStyles';
import { withRowExit } from '../shared/inboxColumns';
import { InboxViewLink } from '../shared/InboxViewLink';
import { MonitoringPatientIdentity } from '../shared/MonitoringPatientIdentity';
import { WaitingCell } from '../shared/WaitingCell';
import { LatestMessageCell } from './LatestMessageCell';
import { FROM_MESSAGE_LIST_STATE, messageViewPath } from './messageListParams';
import type { MessageTableRow } from './messageRows';
import { FrustrationCell, UrgencyCell } from './triage/TriageCells';

// Room for the 108px picker button, the AI marker's slot and padding; Latest message takes the rest.
const TRIAGE_COLUMN_WIDTH = '160px';
const COLUMN_WIDTH = {
    patient: '18%',
    // The 42px ring plus cell padding.
    dm: '72px',
    assignee: '12%',
    latest: 'auto',
    urgency: TRIAGE_COLUMN_WIDTH,
    frustration: TRIAGE_COLUMN_WIDTH,
    waiting: '11%',
    // Closed takes the triage and waiting columns' place.
    closed: '24%',
    view: '8%'
} as const;

type MessageColumn = DataTableColumn<MessageTableRow, DmMessageInboxSort>;

const PATIENT_COLUMN: MessageColumn = {
    ...INBOX_COLUMN_CHROME,
    key: 'patient',
    label: 'Patient',
    width: COLUMN_WIDTH.patient,
    render: (row) => <MonitoringPatientIdentity patient={row.patient} />
};

const DM_COLUMN: MessageColumn = {
    ...INBOX_COLUMN_CHROME,
    key: 'dm',
    label: 'DM',
    width: COLUMN_WIDTH.dm,
    render: (row) => (
        <AlignerMonitoringCell
            current={row.patient.currentAligner}
            total={row.patient.totalAligners}
            monitoringStatus={row.patient.monitoringStatus}
        />
    )
};

function assigneeColumn(rowCount: number): MessageColumn {
    return {
        ...INBOX_COLUMN_CHROME,
        key: 'assignee',
        label: 'Assignee',
        width: COLUMN_WIDTH.assignee,
        // No overflow-hidden: the dropdown renders in-place and would be clipped.
        stopClickPropagation: true,
        render: (row, index) => (
            <NotificationAssigneeCell row={row.patient} dropUp={opensUpwards(index, rowCount)} />
        )
    };
}

const LATEST_MESSAGE_COLUMN: MessageColumn = {
    ...INBOX_COLUMN_CHROME,
    key: 'latest',
    label: 'Latest message',
    width: COLUMN_WIDTH.latest,
    sortable: true,
    sortKey: 'latest',
    render: (row) => <LatestMessageCell row={row.patient} />
};

function urgencyColumn(rowCount: number): MessageColumn {
    return {
        ...INBOX_COLUMN_CHROME,
        key: 'urgency',
        label: 'Priority',
        width: COLUMN_WIDTH.urgency,
        sortable: true,
        sortKey: 'urgency',
        align: 'center',
        stopClickPropagation: true,
        render: (row, index) => (
            <UrgencyCell row={row.patient} dropUp={opensUpwards(index, rowCount)} />
        )
    };
}

function frustrationColumn(rowCount: number): MessageColumn {
    return {
        ...INBOX_COLUMN_CHROME,
        key: 'frustration',
        label: 'Frustration',
        width: COLUMN_WIDTH.frustration,
        sortable: true,
        sortKey: 'frustration',
        align: 'center',
        stopClickPropagation: true,
        render: (row, index) => (
            <FrustrationCell row={row.patient} dropUp={opensUpwards(index, rowCount)} />
        )
    };
}

const WAITING_COLUMN: MessageColumn = {
    ...INBOX_COLUMN_CHROME,
    key: 'waiting',
    label: 'Waiting',
    width: COLUMN_WIDTH.waiting,
    sortable: true,
    sortKey: 'waiting',
    render: (row) => (
        <WaitingCell
            oldestAt={row.patient.oldestAt}
            latestAt={row.patient.latestAt}
            inbox="messages"
        />
    )
};

// Closed rows preview their latest resolved message and sort by when they closed (the API's latest).
const CLOSED_LATEST_MESSAGE_COLUMN: MessageColumn = {
    ...LATEST_MESSAGE_COLUMN,
    sortable: false,
    render: (row) => <LatestMessageCell row={row.patient} closed />
};

const CLOSED_COLUMN: MessageColumn = {
    ...INBOX_COLUMN_CHROME,
    key: 'closed',
    label: 'Closed',
    width: COLUMN_WIDTH.closed,
    sortable: true,
    sortKey: 'latest',
    render: (row) => <ClosureCell resolution={row.patient.lastResolution} />
};

function viewColumn(listSearch: string): MessageColumn {
    return {
        ...INBOX_COLUMN_CHROME,
        key: 'view',
        label: 'Actions',
        headerNode: null,
        width: COLUMN_WIDTH.view,
        align: 'right',
        stopClickPropagation: true,
        render: (row) => (
            <InboxViewLink
                to={messageViewPath(row.patient.patientId, listSearch)}
                state={FROM_MESSAGE_LIST_STATE}
                ariaLabel={`View messages from ${row.patient.patientName}`}
            />
        )
    };
}

function openColumns(rowCount: number): MessageColumn[] {
    return [
        LATEST_MESSAGE_COLUMN,
        urgencyColumn(rowCount),
        frustrationColumn(rowCount),
        WAITING_COLUMN
    ];
}

export function buildMessageColumns(
    rowCount: number,
    listSearch: string,
    status: DmInboxStatus
): MessageColumn[] {
    const statusColumns =
        status === 'closed' ? [CLOSED_LATEST_MESSAGE_COLUMN, CLOSED_COLUMN] : openColumns(rowCount);
    return [
        PATIENT_COLUMN,
        assigneeColumn(rowCount),
        DM_COLUMN,
        ...statusColumns,
        viewColumn(listSearch)
    ].map(withRowExit);
}
